import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Link,
  Box,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Stack,
} from "@mui/material";
import { organizationActions } from "../../../actions";
import Intro from "../../../components/atoms/Intro/Intro.component";
import { apiConstants } from "../../../constants";
import { authHeader, getUserToken } from "../../../helpers";

const OrganizationSingle = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const organization = useSelector(
    (state) => state.organizations?.parameter?.data
  );

  const [collections, setCollections] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [currentCollection, setCurrentCollection] = useState(null);
  const [formState, setFormState] = useState({
    title: "",
    body_html: "",
    handle: "",
    image: "",
    image_url: "",
    published: false,
  });

  // Fetch organization details
  useEffect(() => {
    dispatch(organizationActions.getSingleParameter(id));
  }, [dispatch, id]);

  // Fetch collections on mount and when id changes
  useEffect(() => {
    fetchCollections();
  }, [id]);

  const fetchCollections = () => {
    fetch(`${apiConstants.API_URL}/shopify_clubs/${id}`, {
      method: "GET",
      headers: authHeader(),
    })
      .then((res) => res.json())
      .then((data) => setCollections(data?.data || []))
      .catch((err) => console.error("Error fetching collections:", err));
  };

  // Sync collections endpoint
  const handleSyncCollections = () => {
    fetch(`${apiConstants.API_URL}/shopify_clubs/sync_collection/${id}`, {
      method: "GET", // or "POST" based on your API
      headers: authHeader(),
    })
      .then((res) => res.json())
      .then(() => {
        fetchCollections();
      })
      .catch((err) => console.error("Failed to sync collections:", err));
  };

  const handleOpenAddModal = () => {
    setCurrentCollection(null);
    setFormState({
      title: "",
      body_html: "",
      handle: "",
      image: "",
      image_url: "",
      published: false,
    });
    setOpenModal(true);
  };

  const handleEditCollection = (collection) => {
    setCurrentCollection(collection);
    setFormState({
      title: collection.title,
      body_html: collection.body_html,
      handle: collection.handle,
      image: collection.image || "",
      image_url: collection.image_url || "",
      published: !!collection.published,
    });
    setOpenModal(true);
  };

  const handleDeleteCollection = (collection) => {
    if (window.confirm("Are you sure you want to delete this collection?")) {
      fetch(
        `${apiConstants.API_URL}/shopify_clubs/delete/${collection.id}`,
        {
          method: "DELETE",
          headers: authHeader(),
        }
      )
        .then((res) => res.json())
        .then(() => fetchCollections())
        .catch((err) =>
          console.error("Failed to delete collection:", err)
        );
    }
  };

  const handleViewCollection = (collection) => {
    navigate(`/admin/shopify-management/${id}/collection/${collection.id}`);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setCurrentCollection(null);
  };

  const handleSubmitForm = () => {
    const url = currentCollection
      ? `${apiConstants.API_URL}/shopify_clubs/update/${currentCollection.id}`
      : `${apiConstants.API_URL}/shopify_clubs/create/${id}`;
    const method = currentCollection ? "PUT" : "POST";

    fetch(url, {
      method,
      headers: { ...authHeader(), "Content-Type": "application/json" },
      body: JSON.stringify(formState),
    })
      .then((res) => res.json())
      .then(() => {
        fetchCollections();
        handleCloseModal();
      })
      .catch((err) =>
        console.error("Failed to submit form:", err)
      );
  };

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("image", file);
    fetch(`${apiConstants.API_URL}/images/shopify`, {
      method: "POST",
      headers: { Authorization: `Bearer ${getUserToken()}` },
      body: formData,
    })
      .then((res) => res.json())
      .then((data) =>
        setFormState((prev) => ({
          ...prev,
          image: data.data.path,
          image_url: data.data.url,
        }))
      )
      .catch((err) =>
        console.error("Image upload failed:", err)
      );
  };

  const handleTogglePublished = (e) => {
    setFormState((prev) => ({ ...prev, published: e.target.checked }));
  };

  const breadcrumbs = [
    <Link underline="hover" key="1" color="inherit" href="/admin/shopify-management/">
      Shopify Management
    </Link>,
    <Typography key="2">Store - {organization?.name}</Typography>,
  ];

  return (
    <>
      <Intro
        pageTitle={`Store - ${organization?.name || ""}`}
        pageTitleShort="Store"
        breadcrumbs={breadcrumbs}
        additionalWidgets=""
      />

      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={1}
        sx={{ p: 2 }}
      >
        <Button variant="contained" onClick={handleOpenAddModal}>
          Add New Collection
        </Button>
        <Button variant="outlined" onClick={handleSyncCollections}>
          Sync Collection
        </Button>
      </Stack>

      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {collections.map((collection) => (
            <Grid item xs={12} sm={6} md={4} key={collection.id}>
              <Card sx={{ maxWidth: 345, boxShadow: 3, borderRadius: 2 }}>
                <CardMedia
                  component="img"
                  height="180"
                  image={
                    collection.image_url ||
                    "https://via.placeholder.com/300x180?text=No+Image"
                  }
                  alt={collection.title}
                />
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {collection.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Handle: {collection.handle}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Published: {collection.published ? "Yes" : "No"}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button size="small" onClick={() => handleEditCollection(collection)}>
                    Edit
                  </Button>
                  <Button size="small" color="error" onClick={() => handleDeleteCollection(collection)}>
                    Delete
                  </Button>
                  <Button size="small" onClick={() => handleViewCollection(collection)}>
                    View
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle>
          {currentCollection ? "Edit Collection" : "Add New Collection"}
        </DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Title"
            fullWidth
            value={formState.title}
            onChange={(e) =>
              setFormState((prev) => ({ ...prev, title: e.target.value }))
            }
          />
          <TextField
            margin="dense"
            label="Handle"
            fullWidth
            value={formState.handle}
            onChange={(e) =>
              setFormState((prev) => ({ ...prev, handle: e.target.value }))
            }
          />
          <TextField
            margin="dense"
            label="Body HTML"
            fullWidth
            multiline
            rows={6}
            value={formState.body_html}
            onChange={(e) =>
              setFormState((prev) => ({
                ...prev,
                body_html: e.target.value,
              }))
            }
          />
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" component="label">
              Upload Image
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={onFileChange}
              />
            </Button>
            {formState.image_url && (
              <Box sx={{ mt: 2 }}>
                <img
                  src={formState.image_url}
                  alt="Collection"
                  style={{ maxWidth: "100%", maxHeight: 200 }}
                />
              </Box>
            )}
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={formState.published}
                onChange={handleTogglePublished}
              />
            }
            label="Published"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button onClick={handleSubmitForm}>
            {currentCollection ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrganizationSingle;
